import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { PlayCircle, Upload, Trash2, Video as VideoIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const VIDEO_CATEGORIES = ["Hutbe", "Ligjerata", "Kuran"];

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const VideoGallery = ({ isAdmin, token }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'youtube'
  const [playingVideo, setPlayingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Hutbe',
    file: null,
    youtube_url: ''
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Gabim në ngarkimin e videove');
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (uploadType === 'file') {
      if (!formData.file || !formData.title) {
        toast.error('Ju lutem plotesoni të gjitha fushat');
        return;
      }

      setUploading(true);
      const data = new FormData();
      data.append('title', formData.title);
      data.append('category', formData.category);
      data.append('file', formData.file);

      try {
        await axios.post(`${API}/admin/videos`, data, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Videoja u ngarkua me sukses');
        setUploadOpen(false);
        setFormData({ title: '', category: 'Hutbe', file: null, youtube_url: '' });
        fetchVideos();
      } catch (error) {
        console.error('Error uploading video:', error);
        toast.error('Gabim në ngarkimin e videos');
      } finally {
        setUploading(false);
      }
    } else {
      // YouTube URL
      if (!formData.youtube_url || !formData.title) {
        toast.error('Ju lutem plotesoni të gjitha fushat');
        return;
      }

      const youtubeId = getYouTubeId(formData.youtube_url);
      if (!youtubeId) {
        toast.error('URL e YouTube nuk është e vlefshme');
        return;
      }

      setUploading(true);
      try {
        await axios.post(`${API}/admin/videos/youtube`, {
          title: formData.title,
          category: formData.category,
          youtube_url: formData.youtube_url
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast.success('Videoja e YouTube u shtua me sukses');
        setUploadOpen(false);
        setFormData({ title: '', category: 'Hutbe', file: null, youtube_url: '' });
        fetchVideos();
      } catch (error) {
        console.error('Error adding YouTube video:', error);
        toast.error('Gabim në shtimin e videos YouTube');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDelete = async (videoId) => {
    if (!token) {
      toast.error('Ju lutem hyni si admin për të fshirë video');
      return;
    }
    
    if (!window.confirm('Jeni i sigurt që dëshironi të fshini këtë video?')) return;

    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Videoja u fshi me sukses');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      if (error.response?.status === 401) {
        toast.error('Sesioni ka skaduar. Ju lutem hyni përsëri.');
      } else {
        toast.error('Gabim në fshirjen e videos');
      }
    }
  };

  const getCategoryLabel = (category) => {
    const normalizedCategory = category === 'hutbe'
      ? 'Hutbe'
      : category === 'ligjerate'
        ? 'Ligjerata'
        : category;
    return VIDEO_CATEGORIES.includes(normalizedCategory) ? normalizedCategory : category;
  };

  const previewVideos = videos.slice(0, 3);

  return (
    <section id="video" className="py-12 md:py-16 bg-muted" data-testid="video-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-3">Video Galeria</h2>
            <p className="text-base text-muted-foreground">
              Hutbe dhe ligjrata nga xhamia jonë
            </p>
          </motion.div>

          {isAdmin && (
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent-700" data-testid="upload-video-button">
                  <Upload className="w-4 h-4 mr-2" />
                  Shto Video
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="upload-video-modal" className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Shto Video të Re</DialogTitle>
                </DialogHeader>
                
                {/* Upload Type Toggle */}
                <div className="flex gap-2 p-1 bg-gray-100 rounded-lg mb-4">
                  <button
                    type="button"
                    onClick={() => setUploadType('file')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      uploadType === 'file' 
                        ? 'bg-white shadow text-primary' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Ngarko Skedar
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadType('youtube')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      uploadType === 'youtube' 
                        ? 'bg-white shadow text-red-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <PlayCircle className="w-4 h-4" />
                    YouTube URL
                  </button>
                </div>

                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Titulli</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Titulli i videos"
                      data-testid="video-title-input"
                    />
                  </div>
                  <div className="relative z-[70]">
                    <Label htmlFor="category">Kategoria</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="video-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[80]">
                        {VIDEO_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {uploadType === 'file' ? (
                    <div>
                      <Label htmlFor="file">Skedari Video</Label>
                      <Input
                        id="file"
                        type="file"
                        accept="video/*"
                        onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                        data-testid="video-file-input"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="youtube_url">YouTube URL</Label>
                      <Input
                        id="youtube_url"
                        value={formData.youtube_url}
                        onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                        data-testid="video-youtube-input"
                      />
                      {formData.youtube_url && getYouTubeId(formData.youtube_url) && (
                        <div className="mt-2 rounded-lg overflow-hidden">
                          <img 
                            src={`https://img.youtube.com/vi/${getYouTubeId(formData.youtube_url)}/mqdefault.jpg`}
                            alt="YouTube thumbnail"
                            className="w-full h-32 object-cover"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button type="submit" disabled={uploading} className="w-full" data-testid="video-submit-button">
                    {uploading ? 'Duke ngarkuar...' : uploadType === 'file' ? 'Ngarko' : 'Shto Video'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Duke ngarkuar videot...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl">
            <VideoIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nuk ka video për momentin</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {previewVideos.map((video, index) => {
              const youtubeId = video.youtube_url ? getYouTubeId(video.youtube_url) : null;
              const thumbnailUrl = youtubeId 
                ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
                : 'https://images.unsplash.com/photo-1771536145073-b79d8d6ab218?crop=entropy&cs=srgb&fm=jpg&q=85';
              
              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-card rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`video-card-${video.id}`}
                >
                  <div 
                    className="relative h-36 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group cursor-pointer"
                    style={{
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                    onClick={() => youtubeId && setPlayingVideo(video)}
                  >
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                    {youtubeId && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <PlayCircle className="w-3 h-3" />
                        YouTube
                      </div>
                    )}
                    <PlayCircle className="w-12 h-12 text-white relative z-10 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-heading font-semibold text-base line-clamp-2">{video.title}</h3>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(video.id);
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors p-2 hover:bg-red-50 rounded-full flex-shrink-0"
                          data-testid={`delete-video-${video.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-full">
                        {getCategoryLabel(video.category)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(video.upload_date).toLocaleDateString('sq-AL')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {!loading && videos.length > 0 && (
          <div className="flex justify-center mt-8">
            <Link
              to="/videogaleri"
              className="border border-emerald-800 text-emerald-800 px-6 py-2.5 rounded-lg font-medium hover:bg-emerald-800 hover:text-white transition-colors duration-200"
            >
              Shiko të gjitha videot →
            </Link>
          </div>
        )}

        {/* YouTube Video Player Modal */}
        {playingVideo && getYouTubeId(playingVideo.youtube_url) && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setPlayingVideo(null)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setPlayingVideo(null)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="aspect-video bg-black rounded-xl overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(playingVideo.youtube_url)}?autoplay=1`}
                  title={playingVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <h3 className="text-white text-xl font-semibold mt-4">{playingVideo.title}</h3>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VideoGallery;
